<template>
  <div>
    <exo-form hideClose :root="_self" :clear="false">
      <template v-slot:titleTooltip>
        <exo-help-dialog
          :color="'black--text'"
          :margin="'ma-0'"
          title="Constellation Performance Analysis"
        >
          <tooltipConstellationPerformanceAnalysisMission />
        </exo-help-dialog>
      </template>
      <exo-input-name :root="_self" gt_PerformanceAnalysis-v-step="3" />
      <v-row no-gutters>
        <v-col cols="5">
          <exo-card-form label="Simulation">
            <exo-input-ref-notparametric
              :root="_self"
              name="constellation"
              type="constellation"
              gt_PerformanceAnalysis-v-step="4"
              componentRoute="/analyticalconstellationheterogeneous"
            >
              <exo-help-dialog title="Constellation">
                <tooltipConstellationGeometry />
              </exo-help-dialog>
            </exo-input-ref-notparametric>
            <div gt_PerformanceAnalysis-v-step="38" />
            <exo-form-alert class="mb-2" type="warning" v-if="missingPayload">
              A payload has to be defined in the constellation.
            </exo-form-alert>
            <exo-input-ref-multiple
              :root="_self"
              name="earthMeshes"
              :isParametric="false"
              type="earthMesh"
              gt_PerformanceAnalysis-v-step="39"
              componentRoute="/analyticalconstellationearthmesh"
            />
            <div gt_PerformanceAnalysis-v-step="48" />
            <exo-input-double
              :root="_self"
              name="simulationDuration"
              gt_PerformanceAnalysis-v-step="49"
            >
              <exo-help-dialog title="Simulation duration">
                Amount of time simulated. Setting the simulation duration to a
                too small value could lead too meaningless outputs.
              </exo-help-dialog>
            </exo-input-double>
            <exo-input-date-time :root="_self" name="simulationDate">
              <exo-help-dialog margin="mt-1 ml-2" title="Simulation date">
                The simulation date is used as reference for the definition of
                the RAAN / MLTAN of each planes of the constellation. It is used
                also to compute the Sun position if the sunlight is considered.
              </exo-help-dialog>
            </exo-input-date-time>
          </exo-card-form>
          <div gt_PerformanceAnalysis-v-step="52" />
        </v-col>
        <v-col cols="7">
          <exo-card-form>
            <div class="metrics-computation-container">
              <div class="metrics-computation-label">Metrics Computation</div>
              <div class="metrics-computation-helper">
                <exo-help-dialog title="Metrics">
                  <tooltipRevisitGroundSegments />
                </exo-help-dialog>
              </div>
            </div>
            <exo-input-double :root="_self" name="threshold">
              <exo-help-dialog title="Custom percentile">
                <tooltipCustomPercentile />
                <p>
                  By default, the 50-th, 75-th, 90-th and 95-th percentiles are
                  computed to evaluate the results in different levels of
                  majority of the cases.
                </p>
                <p>
                  <b>Access duration percentiles</b>: for the access duration
                  metric (and only this metric), the percentiles values are
                  inverted. For example, if the 90-th percentile of the revisit
                  duration is equal to 1 minute, it means that 90% of the
                  revisits on that point have a duration greater than 1 minute,
                  and not the other way round.
                </p>
              </exo-help-dialog>
            </exo-input-double>
            <exo-input-double
              :root="_self"
              name="minControlPointVisibilityTime"
            >
              <exo-help-dialog
                title="Minimum point of interest visibility duration"
              >
                A point will be considered seen only if it is visible at least
                for this minimum duration.
              </exo-help-dialog>
            </exo-input-double>
            <exo-input-checkbox :root="_self" name="sunLightUsed">
              <exo-help-dialog
                margin="mb-3 ml-2"
                alignWithParametricField
                title="Use Sun light for payload visibility"
              >
                The sunlight can be taken into account in the performance
                metrics computation by defining an enlightenment condition.
              </exo-help-dialog>
            </exo-input-checkbox>
            <exo-input-double
              v-if="inputs.sunLightUsed"
              :root="_self"
              name="sunZenithAngle"
            >
              <exo-help-dialog title="Sun zenith angle">
                <tooltipSunZenithAngle />
              </exo-help-dialog>
            </exo-input-double>
            <v-row no-gutters>
              <v-col cols="14" gt_PerformanceAnalysis-v-step="50">
                <v-row>
                  <v-col stycols="2">
                    <v-checkbox
                      v-model="inputs.computeRevisit"
                      :label="getFormLabel('computeRevisit')"
                    ></v-checkbox>
                    <div :style="{ marginLeft: '20px' }">
                      <v-row no-gutters>
                        <exo-input-checkbox
                          v-if="inputs.computeRevisit"
                          :root="_self"
                          name="computePointCollectTime"
                        >
                          <exo-help-dialog
                            v-if="inputs.computeRevisit"
                            margin="mb-3 ml-2"
                            alignWithParametricField
                            title="Collect time"
                          >
                            <tooltipCollectTime />
                          </exo-help-dialog>
                        </exo-input-checkbox>
                      </v-row>
                      <v-row no-gutters>
                        <v-col cols="5">
                          <exo-input-checkbox
                            v-if="inputs.computeRevisit"
                            :root="_self"
                            name="computeRevisitEphemerides"
                          >
                          </exo-input-checkbox>

                          <exo-input-checkbox
                            v-if="inputs.computeRevisit"
                            :root="_self"
                            name="computeMeshGeoArcProtection"
                          >
                            <exo-help-dialog
                              v-if="inputs.computeRevisit"
                              margin="mb-3 ml-2"
                              alignWithParametricField
                              title="Geo Arc Protection"
                            >
                              <tooltipGeoArcProtection />
                            </exo-help-dialog>
                          </exo-input-checkbox>

                          <div class="ml-6">
                            <exo-input-double
                              v-if="
                                inputs.computeMeshGeoArcProtection &&
                                inputs.computeRevisit
                              "
                              :root="_self"
                              name="geoArcThresholdAngle"
                            >
                            </exo-input-double>
                          </div>
                        </v-col>
                      </v-row>
                    </div>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </exo-card-form>
          <div class="ground-segment-label">Ground Segment</div>
          <div class="pl-6">
            <v-row>
              <v-checkbox
                v-model="inputs.computeSimplifiedGroundSegment"
                :label="getFormLabel('computeSimplifiedGroundSegment')"
              ></v-checkbox>

              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <v-checkbox
                    v-on="on"
                    v-model="inputs.computeExtendedGroundSegment"
                    :label="getFormLabel('computeExtendedGroundSegment')"
                  ></v-checkbox>
                </template>

                <span>Includes the time between reverse link arrivals</span>
              </v-tooltip>
            </v-row>
            <v-row>
              <exo-form-alert
                class="alert-text"
                type="error"
                v-if="
                  inputs.computeExtendedGroundSegment &&
                  inputs.computeSimplifiedGroundSegment
                "
              >
                Simplified and extended ground segments results can not be
                evaluated at the same time.
              </exo-form-alert>
            </v-row>
            <v-row class="alert-text">
              <exo-form-alert
                type="warning"
                v-if="inputs.computeExtendedGroundSegment"
              >
                The evaluation of the time between reverse link arrivals can be
                very long to compute, ground segment extended should only be
                evaluated on small constellations during a short period.
              </exo-form-alert>
            </v-row>
            <v-row class="export-ephemeris">
              <v-col cols="6">
                <exo-input-checkbox
                  v-if="groundSegmentEphemeridesEvaluation"
                  :root="_self"
                  name="computeGroundStationEphemerides"
                >
                </exo-input-checkbox>
              </v-col>
              <v-col cols="1">
                <exo-help-dialog
                  :margin="'mt-0 ml-2'"
                  v-if="groundSegmentEphemeridesEvaluation"
                  :title="getFormLabel('computeGroundStationEphemerides')"
                >
                  The output file contains the following information:
                  Constellation name, plane index, spacecraft index, start/end
                  of the visualization interval. This mode highlights the
                  visualization intervals between a spacecraft and the ground
                  station network.
                </exo-help-dialog>
              </v-col>
            </v-row>
            <div class="row-spacing">
              <v-row>
                <exo-input-ref-multiple
                  v-if="groundSegmentEvaluation"
                  :root="_self"
                  name="groundStations"
                  :isParametric="false"
                  type="groundStation"
                  gt_PerformanceAnalysis-v-step="51"
                  componentRoute="/analyticalconstellationgroundstation"
                />
              </v-row>
              <v-row>
                <exo-input-double
                  v-if="groundSegmentEvaluation"
                  :root="_self"
                  name="minStationVisibilityTime"
                >
                  <exo-help-dialog title="Minimum station visibility duration">
                    <tooltipConstellationMinimumStationVisibilityDuration />
                  </exo-help-dialog>
                </exo-input-double>
              </v-row>
            </div>
          </div>
        </v-col>
      </v-row>
    </exo-form>
    <v-tour
      name="performanceAnalysisTourMission"
      :steps="steps_perf_analysis_mission"
      :callbacks="customCallbacks"
      :options="noSkipStepOption"
    ></v-tour>
  </div>
</template>

<script>
import formMixin from '../../mixins/forms.js'
import formAnalyticalMixin from '../../mixins/analyticalFoms.js'
import constellationComponentsLoader from '../../components/Forms/analyticalConstellationsComponents.js'
import dataTools from '../../tools/data.js'
import missionTools from '../../store/mission.js'
import fieldsDescription from '../../tools/fieldsDescription.json'
import dataDescription from '../../tools/dataDescription.json'
import { mapGetters, mapActions } from 'vuex'
import tooltipConstellationGeometry from '../../components/Tooltips/ConstellationModule/tooltipConstellationGeometry'
import tooltipConstellationPerformanceAnalysisMission from '../../components/Tooltips/ConstellationModule/tooltipConstellationPerformanceAnalysisMission'
import tooltipRevisitGroundSegments from '../../components/Tooltips/ConstellationModule/tooltipRevisitGroundSegments'
import tooltipCollectTime from '../../components/Tooltips/ConstellationModule/tooltipCollectTime'
import tooltipGeoArcProtection from '../../components/Tooltips/ConstellationModule/tooltipGeoArcProtection.vue'
import tooltipCustomPercentile from '../../components/Tooltips/ConstellationModule/tooltipCustomPercentile'
import tooltipSunZenithAngle from '../../components/Tooltips/ConstellationModule/tooltipSunZenithAngle'
import tooltipConstellationMinimumStationVisibilityDuration from '../../components/Tooltips/ConstellationModule/tooltipConstellationMinimumStationVisibilityDuration'
import guidedTutorialPerformanceAnalysis, {
  steps_perf_analysis_mission
} from '../../components/GuidedTutorials/guidedTutorialPerformanceAnalysis.js'
import { noSkipStepOption } from '../../components/GuidedTutorials/guidedTutorialConstraints.js'

constellationComponentsLoader()

export default {
  name: 'AnalyticalConstellationSimulation',

  mixins: [formMixin, formAnalyticalMixin],

  components: {
    tooltipConstellationPerformanceAnalysisMission,
    tooltipConstellationGeometry,
    tooltipRevisitGroundSegments,
    tooltipCollectTime,
    tooltipCustomPercentile,
    tooltipSunZenithAngle,
    tooltipConstellationMinimumStationVisibilityDuration,
    tooltipGeoArcProtection
  },

  async mounted() {
    this.$root.$on(
      'request-computation-constellation-analysis',
      this.requestComputationConstellationAnalysisHandler
    )
    this.$root.$on('copy-mission', this.copyMissionHandler)
    this.$root.$on('create-mission', this.createMissionHandler)
    this.$root.$on('current-mission-updated', this.currentMissionUpdatedHandler)
    await this.refreshReferences()
    this.refreshForm()
    await this.refreshGroundStations()
    if (this.gt_PerformanceAnalysis.isActive) {
      guidedTutorialPerformanceAnalysis.beginPerformanceAnalysisTour(this)
    }
  },

  beforeDestroy() {
    this.$root.$off(
      'request-computation-constellation-analysis',
      this.requestComputationConstellationAnalysisHandler
    )
    this.$root.$off('copy-mission', this.copyMissionHandler)
    this.$root.$off('create-mission', this.createMissionHandler)
    this.$root.$off(
      'current-mission-updated',
      this.currentMissionUpdatedHandler
    )
  },

  computed: {
    ...mapGetters(['currentMission', 'gt_PerformanceAnalysis']),
    groundSegmentEphemeridesEvaluation() {
      return this.inputs.computeExtendedGroundSegment === true
    },
    groundSegmentEvaluation() {
      return (
        this.inputs.computeSimplifiedGroundSegment === true ||
        this.inputs.computeExtendedGroundSegment === true
      )
    },

    missingPayload() {
      if (this.inputs.constellation) {
        if (this.inputs.constellation.homogeneousConstellationGeometries[0]) {
          if (
            !this.inputs.constellation.homogeneousConstellationGeometries[0]
              .payload
          ) {
            return true
          }
        }
      }
      return false
    },
    inputsValid() {
      let fieldsState = this.verifyPropertiesList(
        this.getRequiredPropertiesList(this.inputs),
        this.inputs,
        true
      )
      fieldsState =
        fieldsState &&
        this.verifyPropertiesList(
          this.getOptionalPropertiesList(this.inputs),
          this.inputs,
          false
        )
      fieldsState =
        fieldsState &&
        this.inputs.earthMeshes != null &&
        this.inputs.earthMeshes.length > 0
      if (
        this.inputs.computeSimplifiedGroundSegment ||
        this.inputs.computeExtendedGroundSegment
      ) {
        const groundStationsSet =
          this.inputs.groundStations != null &&
          this.inputs.groundStations.length > 0
        return (
          this.inputNameState &&
          fieldsState &&
          groundStationsSet &&
          !this.missingPayload
        )
      } else {
        return this.inputNameState && fieldsState && !this.missingPayload
      }
    }
  },

  data() {
    return {
      tableFields: [],

      enumPropertiesList: this.getEnumProperties(),

      modalRef: { content: [] },
      showModal: false,

      constellationsNotParametrics: [],
      groundStationsNotParametrics: [],
      earthMeshsNotParametrics: [],
      steps_perf_analysis_mission: steps_perf_analysis_mission,
      noSkipStepOption: noSkipStepOption,
      customCallbacks: {
        onNextStep: (currentStep) => {
          guidedTutorialPerformanceAnalysis.onNextStepPerfAnalysisMission(
            currentStep,
            this
          )
        },
        onSkip: () => {
          guidedTutorialPerformanceAnalysis.stopAllTours(this)
        },
        onFinish: () => {
          guidedTutorialPerformanceAnalysis.onFinishSubmit(this)
        }
      },

      inputs: {
        name: this.getDefaultValue('name'),
        description: this.getDefaultValue('description'),
        constellation: null,
        groundStations: [],
        computeRevisit: this.getDefaultValue('computeRevisit'),
        computeMeshGeoArcProtection: this.getDefaultValue(
          'computeMeshGeoArcProtection'
        ),
        computePointCollectTime: this.getDefaultValue(
          'computePointCollectTime'
        ),
        computeRevisitEphemerides: this.getDefaultValue(
          'computeRevisitEphemerides'
        ),
        computeGroundStationEphemerides: this.getDefaultValue(
          'computeGroundStationEphemerides'
        ),
        computeSimplifiedGroundSegment: this.getDefaultValue(
          'computeSimplifiedGroundSegment'
        ),
        computeExtendedGroundSegment: this.getDefaultValue(
          'computeExtendedGroundSegment'
        ),
        earthMeshes: [],
        simulationDuration: this.getDefaultValue('simulationDuration'),
        threshold: this.getDefaultValue('threshold'),
        geoArcThresholdAngle: this.getDefaultValue('geoArcThresholdAngle'),
        minStationVisibilityTime: this.getDefaultValue(
          'minStationVisibilityTime'
        ),
        minControlPointVisibilityTime: this.getDefaultValue(
          'minControlPointVisibilityTime'
        ),
        sunZenithAngle: this.getDefaultValue('sunZenithAngle'),
        simulationDate: this.getDefaultValue('simulationDate'),
        sunLightUsed: this.getDefaultValue('sunLightUsed'),
        layout: this.getDefaultValue('layout') // to save the dashboard, should be optional in fieldsdesscription to be automatically cpoied
      }
    }
  },

  methods: {
    ...mapActions(['setCurrentMission', 'start_gt_PerformanceAnalysis']),

    requestComputationConstellationAnalysisHandler() {
      this.onSave()
    },
    copyMissionHandler(mission) {
      this.copyMission(mission)
    },
    createMissionHandler(mission) {
      this.createMission(mission)
    },
    currentMissionUpdatedHandler() {
      this.refreshForm()
    },

    refreshTable() {},

    updateMissionFields(fields) {
      if (fields.typeName === this.getCurrentFormType()) {
        for (let field in fields) {
          this.inputs[field] = fields[field]
        }
      }
    },

    async refreshReferences() {
      await this.refreshConstellations()
      await this.refreshEarthMeshes()
      await this.refreshGroundStations()
    },

    updateGroundSegmentSelection(fieldChanged, otherField) {
      // TODO not working : click on the checkbox or the label result in this funtion being called before or after the update
      if (
        this.inputs[fieldChanged] === true &&
        this.inputs[otherField] === true
      ) {
        this.inputs[otherField] = false
      }
    },

    updateSimplifiedGroundSegment() {
      this.updateGroundSegmentSelection(
        'computeSimplifiedGroundSegment',
        'computeExtendedGroundSegment'
      )
    },

    updateExtendedGroundSegment() {
      this.updateGroundSegmentSelection(
        'computeExtendedGroundSegment',
        'computeSimplifiedGroundSegment'
      )
    },

    copyMission(mission) {
      if (mission && mission.typeName === this.getCurrentFormType()) {
        this.copy(mission)
        this.sendFormToServer()
      }
    },

    refreshForm() {
      if (
        this.currentMission != null &&
        this.currentMission.typeName === this.getCurrentFormType()
      ) {
        if (this.currentMission.id !== this.inputs.id) {
          // different mission reload completely the form
          this.edit(this.currentMission, 0)
        } else {
          // reloading the current mission
          // only refresh the updated fields
          const fieldsToUpdate = [
            this.constellationsNotParametrics,
            this.groundStationsNotParametrics,
            this.earthMeshsNotParametrics
          ]
          missionTools.updateAllReferences(this, fieldsToUpdate)
        }
      }
    },

    async refreshConstellations() {
      let self = this
      await this.$http
        .get(this.getAPI(`constellationdefinitions`))
        .then((response) => {
          self.constellationsNotParametrics = response.data
            .map((obj) => ({ value: obj, text: obj.displayedName }))
            .sort((a, b) =>
              a.text.toLowerCase() < b.text.toLowerCase() ? -1 : 1
            )
        })
        .catch((e) => {
          self.displayMessage(
            'Error getting list of constellations. ',
            e,
            'notification',
            'error'
          )
        })
    },

    async refreshGroundStations() {
      // TODO MFI automatic method for all of these getters
      let self = this
      await this.$http
        .get(this.getAPI(`groundstationdefinitions`))
        .then((response) => {
          self.groundStationsNotParametrics = response.data
            .map((obj) => ({ value: obj, text: obj.displayedName }))
            .sort((a, b) =>
              a.text.toLowerCase() < b.text.toLowerCase() ? -1 : 1
            )
        })
        .catch((e) => {
          self.displayMessage(
            'Error getting list of groundStations. ',
            e,
            'notification',
            'error'
          )
        })
    },

    onSave: function (evt) {
      this.inputs.saving = true
      this.$http
        .put(
          this.getCurrentAPI() + '/' + this.inputs.id,
          this.createObjectFromForm()
        )
        .then((response) => {
          this.inputs.saving = false
          this.$root.$emit('update-mission-list')
          this.$root.$emit('set-current-mission', response.data) // update fields and current mission
          // redirect to dashboard
          this.$router.push({ path: '/analyticalconstellationdashboard' })
        })
        .catch((e) => {
          this.inputs.saving = false
          this.displayMessage(
            'Error creating ' + this.inputs.type + '. ',
            e,
            'notification',
            'error'
          )
        })
    },

    async refreshEarthMeshes() {
      let self = this
      await this.$http
        .get(this.getAPI(`earthmeshdefinitions`))
        .then((response) => {
          self.earthMeshsNotParametrics = response.data
            .map((obj) => ({ value: obj, text: obj.displayedName }))
            .sort((a, b) =>
              a.text.toLowerCase() < b.text.toLowerCase() ? -1 : 1
            )
        })
        .catch((e) => {
          self.displayMessage(
            'Error getting list of earth mesh. ',
            e,
            'notification',
            'error'
          )
        })
    },

    getRefList(param) {
      if (param === 'constellation') {
        return this.constellationsNotParametrics
      }
      if (param === 'earthMesh') {
        return this.earthMeshsNotParametrics
      }
      if (param === 'groundStations') {
        return this.groundStationsNotParametrics
      }
    },

    getCurrentFormTitle() {
      return 'Constellation Performance Analysis'
    },

    getCurrentFormType() {
      return 'ConstellationPerformanceAnalysisRequest'
    },

    createMission(mission) {
      if (mission.typeName === this.getCurrentFormType()) {
        this.clearForm()
        this.updateMissionFields(mission)
        this.sendFormToServer()
      }
    },

    sendFormToServer() {
      this.inputs.saving = true
      this.$http
        .post(this.getCurrentAPI(), this.createObjectFromForm())
        .then((response) => {
          this.inputs.saving = false
          this.$root.$emit('set-current-mission', response.data)
        })
        .catch((e) => {
          this.inputs.saving = false
          this.displayMessage(
            'Error creating ' + this.inputs.type + '. ',
            e,
            'AnalyticalMissionErrorDisplay',
            'error'
          )
        })
    },

    addFieldsListToBaseObject(
      baseObject,
      fieldsList,
      parentObject = this.inputs
    ) {
      for (let field of fieldsList) {
        if (this.getPropertiesDescription()[field] != null) {
          let fieldType = this.getPropertiesDescription()[field].type
          if (
            fieldType === 'enum' ||
            fieldType === 'boolean' ||
            fieldType === 'date'
          ) {
            baseObject[field] = parentObject[field]
          } else if (fieldsDescription[fieldType] != null) {
            baseObject[field] = parentObject[field]
          } else {
            baseObject[field] = dataTools.convertGui2si(
              this.getFieldType(field),
              parentObject[field]
            )
          }
        }
      }
      baseObject.constellation = parentObject.constellation
      return baseObject
    },

    clearLocalFields() {
      for (let field in this.getPropertiesDescription()) {
        if (this.inputs.hasOwnProperty(field)) {
          this.inputs[field] = this.getDefaultValue(field)
          let fieldType = this.getPropertiesDescription()[field].type
          if (dataDescription[fieldType] != null) {
            this.checkParametricValue(this.inputs, field)
          } else if (fieldsDescription[fieldType] != null) {
            this.checkParametricReference(this.inputs, field)
          }
        }
      }
      this.inputs.subMissions = []
    },

    copyLocalFields(item) {
      this.inputs.id = item.id
      this.copyFieldsToBaseObject(item, this.inputs)
    },

    addLocalFieldsFromForm(baseObject, fieldsList) {
      baseObject = this.addFieldsListToBaseObject(baseObject, fieldsList)
      baseObject.computationRequested = true
      return baseObject
    },

    copyFieldsToBaseObject(item, baseObject) {
      for (let field in this.getPropertiesDescription()) {
        if (item.hasOwnProperty(field)) {
          let fieldType = this.getPropertiesDescription()[field].type
          if (dataDescription[fieldType] !== undefined) {
            // parametric value
            baseObject[field] = dataTools.convertSi2gui(
              this.getFieldType(field),
              item[field]
            )
            this.checkParametricValue(baseObject, field)
          } else {
            baseObject[field] = item[field]
            if (fieldsDescription[fieldType] != null) {
              this.checkParametricReference(baseObject, field)
            }
          }
        }
      }
    },
    isExoInputMultiple(item) {
      return item === 'groundStations' || item === 'earthMeshes'
    }
  }
}
</script>
<style>
.alert-text {
  width: 550px;
  word-wrap: break-word;
  padding-left: 26px;
}

.metrics-computation-container {
  display: inline-flex;
  align-items: center;
  justify-content: end;
  margin-top: -110px;
  margin-bottom: -100px;
}

.metrics-computation-label {
  flex-grow: 1;
  font-weight: 700;
  color: rgb(96, 125, 139);
  font-family: 'Arial', fallback-font, sans-serif;
  font-size: 16.38px;
}
.ground-segment-label {
  font-weight: 700;
  color: rgb(96, 125, 139);
  font-family: 'Arial', fallback-font, sans-serif;
  font-size: 16.38px;
  margin-left: 18px;
  margin-top: -5px;
}

.metrics-computation-helper {
  margin-left: 20px;
  margin-bottom: 25px;
}
.row-spacing {
  padding-left: 28px;
  margin-top: -40px;
}
.export-ephemeris {
  margin-bottom: 25px;
  padding-left: 10px;
}
</style>
